import React, { useState, useEffect } from 'react';
import { Cpu, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import Card from '@/components/Card';
import styles from './AIInsightCard.module.css';

interface AIInsightCardProps {
  painAverage: number;
  exerciseCompletion: number;
  proteinIntake: number;
  proteinGoal: number;
  daysInRecovery: number;
}

function generateInsights(painAverage: number, exerciseCompletion: number, proteinIntake: number, proteinGoal: number, daysInRecovery: number): string[] {
  const insights: string[] = [];

  if (painAverage === 0) {
    insights.push('No pain data logged this week. Log your pain levels daily so beryl can give you better insights.');
  } else if (painAverage <= 3) {
    insights.push('Your pain levels are trending well — keep up the great work with your recovery routine.');
  } else if (painAverage <= 6) {
    insights.push('Your pain levels are moderate. Consider reducing high-impact activities and focusing on gentle exercises.');
  } else {
    insights.push('Your pain levels are elevated. Please consult with your healthcare provider about your current symptoms.');
  }

  const proteinPct = proteinGoal > 0 ? (proteinIntake / proteinGoal) * 100 : 0;
  if (proteinPct >= 80) {
    insights.push('Excellent protein intake! This supports bone healing and tissue repair during recovery.');
  } else {
    insights.push(`Consider increasing protein intake to ${proteinGoal}g daily for optimal bone healing. Greek yogurt, lean meats, eggs, and legumes are great sources.`);
  }

  if (exerciseCompletion >= 80) {
    insights.push('Great exercise consistency — regular rehab exercises are key to a successful recovery.');
  } else if (exerciseCompletion >= 50) {
    insights.push('Try to complete more of your prescribed exercises. Consistency helps strengthen the healing bone.');
  } else {
    insights.push('Your exercise completion rate is low. Even gentle movements can significantly aid recovery.');
  }

  if (daysInRecovery <= 14) {
    insights.push(`You're ${daysInRecovery} days into recovery. Focus on rest and following your treatment plan closely during this early phase.`);
  } else if (daysInRecovery <= 42) {
    insights.push(`${daysInRecovery} days of recovery completed. You're in the bone remodelling phase — nutrition and gentle exercise are crucial now.`);
  } else {
    insights.push(`${daysInRecovery} days in — you've come a long way. Stay consistent with your rehab and keep celebrating small wins.`);
  }

  return insights;
}

export default function AIInsightCard({ painAverage, exerciseCompletion, proteinIntake, proteinGoal, daysInRecovery }: AIInsightCardProps) {
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<string[]>([]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setInsights(generateInsights(painAverage, exerciseCompletion, proteinIntake, proteinGoal, daysInRecovery));
      setLoading(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, [painAverage, exerciseCompletion, proteinIntake, proteinGoal, daysInRecovery]);

  return (
    <Card className={styles.card}>
      <button className={styles.header} onClick={() => setExpanded(e => !e)}>
        <div className={styles.titleRow}>
          <div className={styles.iconWrap}>
            <Cpu size={18} color="var(--primary)" />
          </div>
          <div>
            <div className={styles.title}>Recovery Insights</div>
            <div className={styles.subtitle}>Personalised analysis</div>
          </div>
        </div>
        {expanded ? <ChevronUp size={20} color="var(--text-secondary)" /> : <ChevronDown size={20} color="var(--text-secondary)" />}
      </button>

      {expanded && (
        <div className={styles.body}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <span className={styles.loadingText}>Analysing your recovery data...</span>
            </div>
          ) : (
            insights.map((insight, i) => (
              <div key={i} className={styles.insight}>
                <Zap size={14} color="var(--secondary)" style={{ flexShrink: 0, marginTop: 3 }} />
                <p className={styles.insightText}>{insight}</p>
              </div>
            ))
          )}
        </div>
      )}
    </Card>
  );
}
